import React, { useState, Suspense, lazy } from 'react';
import { ArrowRight, Mail, TrendingDown, ShieldCheck, Globe2 } from 'lucide-react';
import PilotForm from './PilotForm';

const Spline = lazy(() => import('@splinetool/react-spline'));

/**
 * Temporary holding page shown while the full site is paused pending
 * finalisation of pre-launch regulatory readiness.
 *
 * Product name is intentionally withheld for now.
 * Toggle in /app/frontend/src/App.js via the MAINTENANCE_MODE flag.
 *
 * REGULATORY NOTE
 * Do NOT use language that implies Māori Fintech "is or will be
 * licensed" by the FMA. FSPR registration is not a licence.
 */

const ATTRIBUTES = [
  {
    icon: TrendingDown,
    eyebrow: 'Why it matters',
    title: 'Over $1 billion leaves Aotearoa every year in payment fees.',
    body:
      'Card schemes, offshore processors and merchant networks quietly carry value out of the country with every swipe, tap and transfer.',
    accent: 'text-red-300',
    bar: 'border-red-500/60',
  },
  {
    icon: ShieldCheck,
    eyebrow: 'How it affects you',
    title: 'That money could easily benefit more families, directly and indirectly.',
    body:
      'Fees that leak overseas are dollars that no longer circulate inside Iwi, businesses, organisations and whānau here in Aotearoa.',
    accent: 'text-amber-300',
    bar: 'border-amber-300/60',
  },
  {
    icon: Globe2,
    eyebrow: 'What we are doing about it',
    title: 'A new B2B payment rail, designed in Aotearoa.',
    body:
      'A closed-loop NZ dollar service built on a production-deployed platform already operating in sovereign environments in the Pacific and the United States.',
    accent: 'text-cyan-300',
    bar: 'border-cyan-300/60',
  },
];

const ComingSoon = () => {
  const [showPilotForm, setShowPilotForm] = useState(false);

  return (
    <div className="min-h-screen bg-black text-white relative overflow-hidden">
      {/* Ambient brand glows */}
      <div className="pointer-events-none absolute inset-0">
        <div className="absolute -top-32 -right-32 w-[480px] h-[480px] rounded-full bg-cyan-400/10 blur-3xl" />
        <div className="absolute bottom-0 -left-32 w-[420px] h-[420px] rounded-full bg-amber-400/[0.06] blur-3xl" />
      </div>

      {/* Top bar */}
      <header className="relative z-10 border-b border-white/5">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-20">
          <div className="flex items-center justify-between h-20">
            <div className="text-2xl font-bold" data-testid="cs-logo">
              Māori<span className="text-cyan-400">Fintech</span>
            </div>
            <button
              onClick={() => setShowPilotForm(true)}
              data-testid="cs-contact"
              className="hidden sm:inline-flex items-center gap-2 px-5 py-2.5 bg-cyan-300 text-black font-semibold hover:bg-cyan-200 transition-colors text-sm"
            >
              Get in touch
              <ArrowRight size={16} />
            </button>
          </div>
        </div>
      </header>

      {/* Hero */}
      <section className="relative z-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-20 pt-20 sm:pt-28 pb-16">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            {/* Left — text */}
            <div className="max-w-3xl">
              <span
                className="inline-flex items-center gap-2 px-4 py-2 bg-cyan-400/10 border border-cyan-400/30 text-xs tracking-[0.3em] uppercase text-cyan-300 font-bold mb-10"
                data-testid="cs-badge"
              >
                <span className="relative flex h-2 w-2">
                  <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-cyan-300 opacity-60" />
                  <span className="relative inline-flex h-2 w-2 rounded-full bg-cyan-300" />
                </span>
                B2B Payment Rail · Launching Soon
              </span>

              <h1 className="display-huge text-white leading-[1.02] mb-8" data-testid="cs-headline">
                Something new is coming for{' '}
                <span className="text-cyan-300">Aotearoa.</span>
              </h1>

              <p className="body-large text-white/80 leading-relaxed max-w-2xl mb-10">
                We&apos;re building infrastructure designed to{' '}
                <span className="text-cyan-300 font-semibold">
                  stop wealth leaking out of Aotearoa
                </span>{' '}
                and keep more of it circulating inside our communities. The name? That&apos;s
                a surprise for launch.
              </p>

              <div className="border-l-2 border-amber-300/60 pl-5 sm:pl-6 py-1 max-w-2xl">
                <div className="text-[11px] tracking-[0.25em] uppercase text-amber-300 font-bold mb-2">
                  Status
                </div>
                <p className="text-white/85 leading-relaxed">
                  We&apos;re in the final stages of{' '}
                  <span className="text-amber-300 font-semibold">
                    finalising our regulatory readiness
                  </span>
                  . The full site will be back online once we&apos;re ready to launch.
                </p>
              </div>
            </div>

            {/* Right — 3D sphere */}
            <div className="relative">
              <div className="w-full h-[360px] sm:h-[480px] lg:h-[600px] relative overflow-visible">
                <Suspense
                  fallback={
                    <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-cyan-900/20 to-purple-900/20 border border-cyan-400/20">
                      <div className="text-center">
                        <div className="w-16 h-16 border-4 border-cyan-400 border-t-transparent rounded-full animate-spin mx-auto mb-4" />
                        <p className="text-cyan-400 font-semibold mb-2">Loading 3D scene</p>
                        <p className="text-white/40 text-sm">First load may take a moment...</p>
                      </div>
                    </div>
                  }
                >
                  <Spline
                    scene="https://prod.spline.design/NbVmy6DPLhY-5Lvg/scene.splinecode"
                    style={{ width: '100%', height: '100%' }}
                  />
                </Suspense>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Three attributes */}
      <section className="relative z-10 border-t border-white/5">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-20 py-20 sm:py-24">
          <div className="grid md:grid-cols-3 gap-6 lg:gap-8">
            {ATTRIBUTES.map(({ icon: Icon, eyebrow, title, body, accent, bar }) => (
              <div
                key={eyebrow}
                className={`relative bg-white/[0.025] border border-white/10 border-l-2 ${bar} p-6 sm:p-7`}
                data-testid={`cs-attr-${eyebrow.toLowerCase().replace(/\s+/g, '-')}`}
              >
                <Icon size={22} className={`${accent} mb-5`} />
                <div className={`text-[10px] tracking-[0.25em] uppercase font-bold mb-3 ${accent}`}>
                  {eyebrow}
                </div>
                <h3 className="text-lg sm:text-xl font-bold text-white leading-tight mb-3">
                  {title}
                </h3>
                <p className="text-white/65 text-sm leading-relaxed">{body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTAs */}
      <section className="relative z-10 border-t border-white/5">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-20 py-16 sm:py-20">
          <div className="max-w-3xl">
            <h2 className="display-medium text-white leading-tight mb-6">
              Want to be one of the first to know when we launch?
            </h2>
            <p className="text-white/65 mb-10 max-w-2xl">
              Register your interest and we&apos;ll be in touch as soon as we&apos;re ready to
              launch. Especially if you lead an Iwi or large organisation thinking
              about your payment future.
            </p>

            <div className="flex flex-col sm:flex-row gap-4">
              <button
                onClick={() => setShowPilotForm(true)}
                data-testid="cs-register-cta"
                className="inline-flex items-center justify-center gap-2 px-7 py-4 bg-cyan-300 text-black font-bold hover:bg-cyan-200 transition-colors"
              >
                Register your interest
                <ArrowRight size={18} />
              </button>
              <a
                href="mailto:larz@maorifintech.co.nz"
                data-testid="cs-email"
                className="inline-flex items-center justify-center gap-2 px-7 py-4 border border-white/25 text-white hover:bg-white/5 transition-colors"
              >
                <Mail size={18} />
                larz@maorifintech.co.nz
              </a>
            </div>

            {/* Trust strip */}
            <div className="grid sm:grid-cols-3 gap-6 pt-12 mt-12 border-t border-white/10 max-w-2xl">
              <div>
                <div className="text-[10px] tracking-[0.25em] uppercase text-white/40 font-bold mb-2">
                  Operator
                </div>
                <div className="text-white/85 text-sm leading-snug">Māori Fintech</div>
              </div>
              <div>
                <div className="text-[10px] tracking-[0.25em] uppercase text-white/40 font-bold mb-2">
                  Platform
                </div>
                <div className="text-white/85 text-sm leading-snug">
                  Pacific Technology Stack
                </div>
              </div>
              <div>
                <div className="text-[10px] tracking-[0.25em] uppercase text-white/40 font-bold mb-2">
                  Currency
                </div>
                <div className="text-white/85 text-sm leading-snug">
                  New Zealand dollars (NZD)
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="relative z-10 border-t border-white/10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-20 py-8">
          {/* Regulatory disclosure */}
          <div className="mb-6 pb-6 border-b border-white/5" data-testid="cs-regulatory-disclosure">
            <p className="text-white/55 text-[11px] leading-relaxed max-w-4xl">
              <span className="text-white/75 font-semibold">Regulatory note:</span>{' '}
              Māori Fintech intends to be registered on the Financial Service Providers Register
              (FSPR). Any product information shown on this site is indicative only and subject
              to completion of our regulatory readiness.
            </p>
          </div>

          <div className="flex flex-col sm:flex-row gap-4 sm:items-center sm:justify-between">
            <p className="text-white/45 text-xs">
              © 2025 Māori Fintech · Aotearoa New Zealand
            </p>
            <p className="text-white/45 text-xs">
              Designed with respect for tikanga Māori.
            </p>
          </div>
        </div>
      </footer>

      {showPilotForm && (
        <PilotForm isOpen={showPilotForm} onClose={() => setShowPilotForm(false)} />
      )}
    </div>
  );
};

export default ComingSoon;
